import {forwardRef} from 'react';
import {useReducedMotion, useScroll, useTransform} from 'motion/react';
import * as motion from 'motion/react-m';
import useTranslation from 'next-translate/useTranslation';

import heroLeftAvif from '@/assets/images/home/keywordAndLocationHero/left_image.avif';
import heroLeftPng from '@/assets/images/home/keywordAndLocationHero/left_image.png';
import heroLeftWebp from '@/assets/images/home/keywordAndLocationHero/left_image.webp';
import heroRightAvif from '@/assets/images/home/keywordAndLocationHero/right_image.avif';
import heroRightPng from '@/assets/images/home/keywordAndLocationHero/right_image.png';
import heroRightWebp from '@/assets/images/home/keywordAndLocationHero/right_image.webp';
import {type FulfillmentDetailAddressInput} from '@/graphql/types';
import StartOrderFormKeywordLocation from './StartOrderFormKeywordLocation';

type HeroProps = {
  geoipRecommendedDefaultAddress?: FulfillmentDetailAddressInput | null;
  shouldCropImagesOnScroll?: boolean;
};

const Hero = forwardRef<HTMLElement, HeroProps>(function Hero(
  {geoipRecommendedDefaultAddress, shouldCropImagesOnScroll = false},
  ref,
) {
  const {t} = useTranslation('home');
  const prefersReducedMotion = useReducedMotion();
  const {scrollY} = useScroll();
  const cropDistance = shouldCropImagesOnScroll && !prefersReducedMotion ? 48 : 0;
  const imageY = useTransform(scrollY, [0, 300], [0, cropDistance]);

  return (
    <header
      ref={ref}
      className="relative flex overflow-hidden bg-green-700 max-[639px]:min-h-[calc(100dvh-5rem)]"
    >
      <picture>
        <source srcSet={heroLeftAvif.src} type="image/avif" />
        <source srcSet={heroLeftWebp.src} type="image/webp" />

        {/* Fallback */}
        <motion.img
          alt=""
          data-testid="hero-image"
          style={{y: imageY}}
          className="pointer-events-none absolute bottom-0 left-0 hidden h-[635px] w-auto desktop1279:block"
          decoding="async"
          src={heroLeftPng.src}
          width={heroLeftPng.width}
          height={heroLeftPng.height}
        />
      </picture>

      <picture>
        <source srcSet={heroRightAvif.src} type="image/avif" />
        <source srcSet={heroRightWebp.src} type="image/webp" />

        {/* Fallback */}
        <motion.img
          alt=""
          data-testid="hero-image"
          style={{y: imageY}}
          className="pointer-events-none absolute bottom-0 right-0 hidden h-[575px] w-auto desktop1279:block"
          decoding="async"
          src={heroRightPng.src}
          width={heroRightPng.width}
          height={heroRightPng.height}
        />
      </picture>

      <div className="relative mx-auto my-8 max-w-full max-[639px]:w-full min-[640px]:my-[72px] desktop924:my-24 desktop1248:my-32">
        <div className="mx-auto flex w-full flex-col min-[769px]:max-w-[880px] min-[769px]:gap-2 min-[1036px]:gap-8">
          <h1 className="max-w-[960px] text-left text-center align-middle font-libre-franklin text-[32px]/[38px] font-bold text-green-300 min-[769px]:text-5xl min-[769px]:leading-tight">
            <span className="block text-white">{t('headlineLine1')}</span>
            <span className="block">{t('headlineLine2')}</span>
          </h1>

          <div className="mt-6 min-[769px]:mt-0">
            <StartOrderFormKeywordLocation
              geoipRecommendedDefaultAddress={geoipRecommendedDefaultAddress}
            />
          </div>
        </div>
      </div>
    </header>
  );
});

export default Hero;
